import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { MdContentCopy } from 'react-icons/md'
import Editor from '@monaco-editor/react'
import type { FormikErrors } from 'formik'
import { Field, Form, Formik } from 'formik'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { object, string } from 'yup'
import useToast from '../../../hooks/useToast'
import { UserContext } from '../../../providers'
import CopyTooltip from '../../CopyTooltip'

interface ExtensionPreset {
  id?: string
  name: string
  description: string
  config: string // JSON string for form handling (single extension config object)
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  initialValues?: {
    id?: string
    name: string
    description: string
    config: Record<string, any>
  }
  readOnly?: boolean
}

const FormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  initialValues,
  readOnly = false,
}) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const [formError, setFormError] = useState<string>('')

  const isEditMode = !!initialValues

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const formInitialValues = useMemo<ExtensionPreset>(() => {
    if (initialValues) {
      return {
        id: initialValues.id,
        name: initialValues.name,
        description: initialValues.description,
        config: JSON.stringify(initialValues.config, null, 2),
      }
    }

    return {
      name: '',
      description: '',
      config: '{}',
    }
  }, [initialValues])

  const validationSchema = useMemo(
    () =>
      object().shape({
        name: string().required(t('name_required', 'Name is required')),
        description: string().required(
          t('description_required', 'Description is required'),
        ),
        config: string().required(
          t('config_required', 'Extension configuration is required'),
        ),
      }),
    [t],
  )

  const validateForm = useCallback(
    (values: ExtensionPreset) => {
      const errors: FormikErrors<ExtensionPreset> = {}

      try {
        validationSchema.validateSync(values, {
          strict: true,
          abortEarly: false,
        })
      } catch (err) {
        ;(err as any)?.inner?.forEach(errObject => {
          errors[errObject.path] = errObject.message
        })
      }

      // Config JSON validation
      if (values.config.trim()) {
        try {
          const parsed = JSON.parse(values.config)
          if (typeof parsed !== 'object' || Array.isArray(parsed)) {
            errors.config = t(
              'config_must_be_object',
              'Config must be an object',
            )
          } else {
            // Check for saved-extension type
            if (parsed && parsed.name === 'saved-extension') {
              errors.config = t(
                'cannot_use_saved_extension_type',
                'Cannot use saved-extension type in extension preset',
              )
            }
          }
        } catch (e) {
          errors.config = t('invalid_json', 'Invalid JSON format')
        }
      }

      return errors
    },
    [validationSchema, t],
  )

  return (
    <Formik<ExtensionPreset>
      initialValues={formInitialValues}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true)
        setFormError('')

        try {
          const config = JSON.parse(values.config)
          const payload = {
            name: values.name.trim(),
            description: values.description.trim(),
            config,
          }

          let result
          if (isEditMode && values.id) {
            // Update existing extension
            result = await request.patch(
              `/api/v1/saved-extensions/${values.id}`,
              payload,
            )
          } else {
            // Create new extension
            result = await request.post('/api/v1/saved-extensions', payload)
          }

          if (request.error) {
            // Handle error
            if ([400, 403, 404].includes(response.status)) {
              setFormError(
                result?.message ||
                  t(
                    'error_saving_extension',
                    'Error saving extension, please try again after sometime',
                  ),
              )
            } else {
              setFormError(
                t(
                  'error_saving_extension',
                  'Error saving extension, please try again after sometime',
                ),
              )
            }
            setSubmitting(false)
          } else {
            // Success
            toast({
              description: isEditMode
                ? t(
                    'saved_extension_updated_successfully',
                    'Saved extension updated successfully.',
                  )
                : t(
                    'saved_extension_created_successfully',
                    'Saved extension created successfully.',
                  ),
              status: 'success',
              isClosable: true,
              duration: 5000,
              position: 'top-right',
              variant: 'outline',
            })
            setFormError('')
            onClose()
            onSuccess()
          }
        } catch (err: any) {
          setFormError(
            err?.message ||
              t(
                'error_saving_extension',
                'Error saving extension, please try again after sometime',
              ),
          )
          setSubmitting(false)
        }
      }}
      validate={validateForm}
      validateOnMount
    >
      {({
        isValid,
        isSubmitting,
        resetForm,
        handleSubmit,
        setFieldValue,
        dirty,
      }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            onClose={() => {
              onClose()
              setFormError('')
            }}
            size="4xl"
            scrollBehavior="inside"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                {isEditMode
                  ? t('edit_saved_extension', 'Edit saved extension')
                  : t('create_saved_extension', 'Create saved extension')}
              </ModalHeader>
              <ModalCloseButton />

              <ModalBody>
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <VStack spacing={4} align="stretch">
                  {isEditMode && initialValues?.id && (
                    <Box>
                      <Text
                        fontSize="14px"
                        color="imagekitGray.900"
                        mb={2}
                        fontWeight="medium"
                      >
                        {t('extension_id', 'Extension ID')}
                      </Text>
                      <HStack spacing={2}>
                        <Text fontSize="sm" color="imagekitGray.900">
                          {initialValues.id}
                        </Text>
                        <CopyTooltip
                          label={t('copy_extension_id', 'Copy extension ID')}
                          successLabel={t('copied', 'Copied!')}
                          copyValue={initialValues.id}
                          placement="top"
                        >
                          <Box
                            as="button"
                            type="button"
                            px={1}
                            pt={1}
                            pb={2}
                            borderRadius="md"
                            cursor="pointer"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Icon
                              as={MdContentCopy}
                              boxSize={3.5}
                              color="imagekitGray.900"
                            />
                          </Box>
                        </CopyTooltip>
                      </HStack>
                    </Box>
                  )}

                  {/* Name Field */}
                  <Field name="name">
                    {({ field, meta }) => (
                      <FormControl
                        isInvalid={meta.error && meta.touched}
                        isRequired
                      >
                        <FormLabel htmlFor="name">
                          {t('extension_name', 'Name')}
                        </FormLabel>
                        <Input
                          {...field}
                          placeholder={t(
                            'extension_name_placeholder',
                            'Enter extension name',
                          )}
                          isReadOnly={readOnly}
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  {/* Description Field */}
                  <Field name="description">
                    {({ field, meta }) => (
                      <FormControl
                        isInvalid={meta.error && meta.touched}
                        isRequired
                      >
                        <FormLabel htmlFor="description">
                          {t('extension_description', 'Description')}
                        </FormLabel>
                        <Textarea
                          {...field}
                          placeholder={t(
                            'extension_description_placeholder',
                            'Describe what this extension configuration does',
                          )}
                          rows={3}
                          isReadOnly={readOnly}
                        />
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Field name="config">
                    {({ field, meta }) => (
                      <FormControl
                        isInvalid={meta.error && meta.touched}
                        isRequired
                      >
                        <FormLabel htmlFor="config">
                          {t(
                            'extension_config',
                            'Extension Configuration (JSON Object)',
                          )}
                        </FormLabel>
                        <Box
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                        >
                          <Editor
                            height="400px"
                            defaultLanguage="json"
                            value={field.value}
                            onChange={value =>
                              setFieldValue('config', value ?? '{}')
                            }
                            options={{
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                              fontSize: 14,
                              formatOnPaste: true,
                              formatOnType: true,
                              readOnly: readOnly,
                              scrollbar: {
                                alwaysConsumeMouseWheel: false,
                              },
                            }}
                            theme="vs-dark"
                          />
                        </Box>
                        <FormErrorMessage>{meta.error}</FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </VStack>
              </ModalBody>

              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  variant="outline"
                  onClick={() => {
                    resetForm()
                    onClose()
                    setFormError('')
                  }}
                  disabled={isSubmitting}
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  colorScheme="appBlue"
                  onClick={() => handleSubmit()}
                  isDisabled={!isValid || !dirty || isSubmitting || readOnly}
                  isLoading={isSubmitting}
                  loadingText="Submitting"
                >
                  {isEditMode ? t('update', 'Update') : t('create', 'Create')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Form>
      )}
    </Formik>
  )
}

export default FormModal
